import { useEffect, useRef, useState } from 'react'
import {
  RECORDER_CHANNELS,
  TRACE_WINDOWS,
  TraceBuffer,
  sampleAt,
  type Simulation,
  type TraceWindow,
} from '@/domain/hs1'
import { Alert, Badge, Button, Card } from '@/components/ui'
import { StagePanel } from '@/components/stage'
import { CycleChart } from '@/sim/CycleChart'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'

/**
 * Recorder — der Zeitschrieb.
 *
 * WAS DIESE SEITE KANN UND DIE SIMULATIONSSEITE NICHT. Dort steht ein
 * geschlossener Zyklus: die Kurve endet, wo sie anfängt. Hier läuft die Zeit,
 * und wenn jemand mitten im Schrieb die Gangart wechselt oder einen Gelenkpunkt
 * verschiebt, steht der ÜBERGANG in der Kurve. Das ist der einzige Ort, an dem
 * man sieht, was eine Änderung wirklich anrichtet.
 *
 * Es ist derselbe Rechenkern. Nur die Achse ist eine andere.
 *
 * DIE UHR LÄUFT AUSSERHALB VON REACT. Fünfzigmal je Sekunde einen Zustand zu
 * setzen hiesse, den Baum fünfzigmal je Sekunde neu zu bauen. Der Puffer ist
 * deshalb eine Referenz, und nur das Diagramm wird im Takt neu gezeichnet —
 * über einen Zähler, der genau dafür da ist.
 */

/** Wie oft abgetastet wird, Hz. */
const RATE = 50

/** Wie oft neu gezeichnet wird, Hz. Weniger als abgetastet — das Auge merkt es nicht. */
const REDRAW = 20

export function RecorderPage() {
  const { state, simulation } = useSimulation()

  const [running, setRunning] = useState(false)
  const [seconds, setSeconds] = useState<number>(4)
  const [selected, setSelected] = useState<string[]>(['knee.angle', 'knee.moment', 'grf'])
  const [, setTick] = useState(0)

  const buffer = useRef(new TraceBuffer())
  const phase = useRef(0)
  const clock = useRef(0)

  /*
    Die laufende Simulation in einer Referenz. Der Aufzeichnungstakt liest sie
    je Abtastpunkt; sie über die Abhängigkeiten der Schleife zu ziehen würde die
    Schleife bei jeder Änderung neu aufsetzen — und dabei die Phase verlieren,
    also genau den Übergang zerstören, den man sehen will.
  */
  const current = useRef<Simulation>(simulation)
  current.current = simulation

  /*
    JEDE ÄNDERUNG BEKOMMT EINE MARKE. Das ist der eigentliche Wert dieser Seite:
    ohne die Marke sieht man im Schrieb einen Knick und weiss nicht, ob er aus
    der Bewegung kommt oder aus der Bedienung.
  */
  const lastGait = useRef(state.gait)
  useEffect(() => {
    if (state.gait === lastGait.current) return
    if (buffer.current.length > 0) {
      buffer.current.mark(clock.current, 'change', state.gait)
    }
    lastGait.current = state.gait
  }, [state.gait])

  useEffect(() => {
    if (!running) return

    let frame = 0
    let last = performance.now()
    let pending = 0
    let sinceRedraw = 0

    const step = (time: number) => {
      const delta = Math.min(0.25, (time - last) / 1000)
      last = time
      pending += delta
      sinceRedraw += delta

      const dt = 1 / RATE
      /*
        Feste Schrittweite statt Bildtakt. Ein ausgelasteter Rechner liefert
        vierzig Bilder je Sekunde, ein ruhiger sechzig; würde man den Bildtakt
        durchreichen, hinge die Zeitachse des Schriebs an der Rechnerlast. Die
        Deckelung auf 0,25 s fängt den Fall ab, dass der Reiter im Hintergrund
        lag — sonst holt die Schleife eine Minute in einem Bild nach.
      */
      while (pending >= dt) {
        pending -= dt
        clock.current += dt

        const simulation = current.current
        const cycleTime = simulation.gait.cycleTime
        if (cycleTime > 0) phase.current = (phase.current + dt / cycleTime) % 1

        /*
          DIE PERIODE, NICHT DIE REIHENLÄNGE. Winkel und Momente tragen den
          Schlusspunkt mit (121 Werte für 120 Schritte), Bodenkraft und
          Lastanteil nicht. Ohne die Periode liefe der Winkel um ein
          Hundertzwanzigstel je Zyklus gegen die Bodenkraft — nach zehn Zyklen
          eine Zehntelphase, und die Diagramme nebeneinander wären falsch.
        */
        const period = simulation.dynamics.samples
        const values: Record<string, number> = {}
        for (const channel of RECORDER_CHANNELS) {
          const track = channel.read(simulation)
          values[channel.key] = track ? sampleAt(track, phase.current, period) : NaN
        }
        buffer.current.push(clock.current, phase.current, values)
      }

      if (sinceRedraw >= 1 / REDRAW) {
        sinceRedraw = 0
        setTick((n) => n + 1)
      }

      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [running])

  const view: TraceWindow = buffer.current.window(seconds)
  const channels = RECORDER_CHANNELS.filter((c) => selected.includes(c.key))

  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  const cycles = view.marks.filter((m) => m.kind === 'cycle').length
  const changes = view.marks.filter((m) => m.kind === 'change')

  return (
    <div className="stack">
      <PageHead
        title="Recorder"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            <Button
              size="sm"
              variant={running ? 'secondary' : 'primary'}
              onClick={() => setRunning((r) => !r)}
            >
              {running ? 'Anhalten' : buffer.current.length ? 'Weiter' : 'Aufzeichnen'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                buffer.current.clear()
                clock.current = 0
                phase.current = 0
                setTick((n) => n + 1)
              }}
            >
              Leeren
            </Button>
          </div>
        }
      />

      {/* Das Bühnenfenster steht auf jeder Seite, auf der man die Bewegung
          verstellt. Beim Scrollen heftet es sich oben an: der Kopftext wird
          einmal gelesen, die Figur wird immer gebraucht. */}
      <StagePanel />

      {buffer.current.length === 0 && (
        <Alert tone="info" title="Noch nichts aufgezeichnet">
          Aufzeichnen starten, dann in einem anderen Reiter die Gangart wechseln oder einen Wert
          verstellen. Der Schrieb läuft weiter und hält den Übergang fest — das ist der Unterschied
          zur Simulationsseite, wo jede Kurve für sich in einem geschlossenen Zyklus steht.
        </Alert>
      )}

      <div className="metrics">
        <Metric
          label="Aufgezeichnet"
          value={`${num(clock.current, 1)} s`}
          note={running ? 'läuft' : buffer.current.length ? 'angehalten' : 'bereit'}
        />
        <Metric label="Fenster" value={`${seconds} s`} note={`${cycles} Zyklusgrenzen darin`} />
        <Metric
          label="Änderungen im Fenster"
          value={changes.length}
          note={changes.length ? changes.map((c) => c.label).join(' · ') : 'keine'}
        />
        <Metric
          label="Zyklusdauer"
          value={`${num(simulation.gait.cycleTime, 2)} s`}
          note={`${num(simulation.metrics.cadence, 0)} Schritte/min`}
        />
      </div>

      <Card
        title="Kanäle und Fenster"
      >
        <div className="stack stack--tight">
          <div className="recorder__chips">
            {RECORDER_CHANNELS.map((channel) => (
              <Button
                key={channel.key}
                size="sm"
                variant={selected.includes(channel.key) ? 'primary' : 'ghost'}
                onClick={() => toggle(channel.key)}
              >
                {channel.label}
              </Button>
            ))}
          </div>
          <div className="recorder__chips">
            {TRACE_WINDOWS.map((value) => (
              <Button
                key={value}
                size="sm"
                variant={seconds === value ? 'primary' : 'ghost'}
                onClick={() => setSeconds(value)}
              >
                {value} s
              </Button>
            ))}
            <Badge tone="neutral">
              {RATE} Hz · Puffer {Math.round(buffer.current.capacity / RATE)} s
            </Badge>
          </div>
        </div>
      </Card>

      {channels.length === 0 ? (
        <Card>
          <p className="page-lead">Kein Kanal gewählt.</p>
        </Card>
      ) : (
        <div className="chart-grid">
          {channels.map((channel) => (
            <Card key={channel.key}>
              <CycleChart
                title={channel.label}
                unit={channel.unit}
                height={190}
                xDomain={[view.from, view.to]}
                xUnit="s"
                xName="der Zeit"
                xLabel="Zeit"
                marks={view.marks}
                series={[
                  {
                    key: channel.key,
                    label: channel.label,
                    values: view.series[channel.key] ?? new Float32Array(0),
                    tone: channel.tone,
                  },
                ]}
              />
            </Card>
          ))}
        </div>
      )}

      <Card title="Lesehinweise">
        <div className="balance__notes">
          <div>
            <span className="eyebrow">Die beiden Markensorten</span>
            <p className="page-lead">
              Gepunktet: eine Zyklusgrenze — dort fängt ein neuer Doppelschritt an. Durchgezogen und
              im Akzent: eine Änderung durch Sie, mit dem Namen der neuen Gangart. Zyklusgrenzen
              sind Takt, Änderungen sind Ereignisse; nur die zweiten sind der Grund, warum man einen
              Zeitschrieb aufmacht.
            </p>
          </div>
          <div>
            <span className="eyebrow">Was der Puffer hält</span>
            <p className="page-lead">
              {Math.round(buffer.current.capacity / RATE)} Sekunden bei {RATE} Hz, als Ring. Was
              älter ist, wird überschrieben. Die Fensterbreite bestimmt nur den Ausschnitt, nicht
              die Aufzeichnung.
            </p>
          </div>
          <div>
            <span className="eyebrow">Die Zeitachse stimmt</span>
            <p className="page-lead">
              Abgetastet wird mit fester Schrittweite und nicht im Bildtakt. Ein ausgelasteter
              Rechner liefert vierzig Bilder je Sekunde, ein ruhiger sechzig; hinge die Aufzeichnung
              daran, hinge die Zeitachse an der Rechnerlast. Für die Darstellung wird zusätzlich auf
              ein festes Raster interpoliert.
            </p>
          </div>
          <div>
            <span className="eyebrow">Was hier nicht steht</span>
            <p className="page-lead">
              Ein Anfahren aus dem Stand. Der Rechenkern rechnet geschlossene Zyklen; der
              Zeitschrieb zeigt den Wechsel zwischen ihnen, nicht die Beschleunigung dazwischen. Ein
              echter Anlauf wäre ein zweiter Betriebsfall im Kern — nicht mehr Formeln, aber ein
              Zustandsverlauf statt eines Zustands.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
